import {
  startUpload,
  finishUpload,
  deleteFile,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/FileReference/FileReferenceService';

interface ICommonInfo {
  fileId: string;
  fileName: string;
}

export interface IFileUploadProgressInfo extends ICommonInfo {
  loaded: number;
  total: number;
}
export interface IFileUploadCompleteInfo extends ICommonInfo {
  success: boolean;
}
export interface IFileUploadErrorInfo extends ICommonInfo {
  error: string;
}

export interface IFileUploaderCallBack {
  onProgress?: (info: IFileUploadProgressInfo) => void;
  onComplete?: (info: IFileUploadCompleteInfo) => void;
  onError?: (info: IFileUploadErrorInfo) => void;
}

export interface IFileUploadOptions {
  file: File;
  chunkSize?: number;
  callBacks?: IFileUploaderCallBack;
}

export class ChunkUploader {
  private uploaded: number = 0;
  private fileId: string = '';
  private userContext: any;
  private baseUrl: string;
  private isCanceled: boolean = false;
  private chunkSize: number;
  private controller: AbortController;
  constructor(private options: IFileUploadOptions) {
    this.userContext = Decisions.getCurrentUserContext();
    this.baseUrl = Decisions.DataTypes.baseURL;
    this.chunkSize = this.options.chunkSize || 1 * 1024 * 1024;
    this.controller = new AbortController(); // setup AbortController
  }

  public async startUpload() {
    const reader = new FileReader();
    reader.onload = () => {
      this.onReaderLoad();
    };
    reader.readAsArrayBuffer(this.options.file);
  }

  public cancel() {
    this.controller.abort();
    if (this.fileId) deleteFile({ id: this.fileId });
  }

  private async onReaderLoad() {
    try {
      const fileReference = await startUpload({
        fileDateTime: new Date(),
        size: this.options.file.size,
        fileName: this.options.file.name,
      });
      this.fileId = fileReference.Id;
      this.run();
    } catch (err) {
      this.onError(`Error starting upload : ${err}`);
    }
  }

  private async run() {
    const chunkCount = this.getChunkCount();
    let chunkIndex = 0;
    while (chunkIndex < chunkCount && !this.isCanceled) {
      const chunk = this.getChunk(chunkIndex);
      // Chunks should be uploaded sequentially, so we disable es-lint rule here.
      // eslint-disable-next-line no-await-in-loop
      await this.uploadChunk(chunk);
      chunkIndex++;
    }
    if (!this.isCanceled) {
      await finishUpload({ id: this.fileId });
      this.onComplete();
    }
  }

  private async uploadChunk(chunk: Blob) {
    //we can not use proxy to make this servie call because the content-type for this call needs to 'application/octet-stream;'
    //In the generated proxies the content-type is always 'application/json;'
    try {
      const url = `${this.baseUrl}/FileReferenceService/JS/UploadChunkInStream`;
      await fetch(url, {
        signal: this.controller.signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          id: this.fileId,
          context: JSON.stringify(this.userContext),
        },
        body: chunk,
      });
      this.onProgress(chunk);
    } catch (err) {
      this.isCanceled = true;
      this.onError(err as string);
    }
  }

  private getChunkCount(): number {
    return Math.ceil(this.options.file.size / this.chunkSize);
  }

  private getChunk(chunkIndex: number): Blob {
    const start = chunkIndex * this.chunkSize;
    let finish = (chunkIndex + 1) * this.chunkSize;

    if (this.options.file.size < finish) {
      finish = this.options.file.size;
    }

    return this.options.file.slice(start, finish);
  }

  private onError(error: string) {
    if (this.options.callBacks && this.options.callBacks.onError && error !== 'canceled') {
      this.options.callBacks.onError({
        error,
        fileId: this.fileId,
        fileName: this.options.file.name,
      });
    }
  }

  private onProgress(chunk: Blob) {
    this.uploaded += chunk.size;
    if (this.options.callBacks && this.options.callBacks.onProgress) {
      this.options.callBacks.onProgress({
        fileId: this.fileId,
        fileName: this.options.file.name,
        loaded: this.uploaded,
        total: this.options.file.size,
      });
    }
  }

  private onComplete() {
    if (this.options.callBacks && this.options.callBacks.onComplete) {
      this.options.callBacks.onComplete({
        fileId: this.fileId,
        fileName: this.options.file.name,
        success: true,
      });
    }
  }
}
